// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  DataTable,
  FieldSearchSelect,
  FieldText,
  Modal,
  SectionCard,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";

function OwnershipsSection({ data: resource, onUpdate, isLoading }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["resources", "community"]);

  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: data && selectAllMembers(data) }),
    }
  );

  const ownerships = resource?.ownerships ?? [];
  const totalOwnership = resource?.total_ownership ?? 0;

  // Modal state
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState("CREATE");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [percOwnership, setPercOwnership] = useState(0);

  const handleClose = () => {
    setOpen(false);
    setActionType("CREATE");
    setSelectedMemberId(null);
    setPercOwnership(0);
  };

  // Members not already owning this resource (for the add dropdown)
  const ownedMemberIds = new Set(ownerships.map((o) => o.user_id));
  const availableMembers = (members ?? [])
    .filter((m) => !ownedMemberIds.has(m.id))
    .map((m) => ({ value: m.id, label: m.name }));

  // Current owner being edited (for edit modal dropdown display)
  const editingMemberOption = (members ?? [])
    .filter((m) => m.id === selectedMemberId)
    .map((m) => ({ value: m.id, label: m.name }));

  // Validation: remaining capacity
  // prettier-ignore
  const editingCurrentPerc =
    actionType === "UPDATE"
      ? (ownerships.find((o) => o.user_id === selectedMemberId)?.perc_ownership ?? 0)
      : 0;
  const maxPercAllowed =
    actionType === "CREATE" ? 100 - totalOwnership : 100 - totalOwnership + editingCurrentPerc;
  const isPercValid = parseFloat(percOwnership) > 0 && parseFloat(percOwnership) <= maxPercAllowed;

  // ADD
  const onAddClicked = () => {
    setActionType("CREATE");
    setSelectedMemberId(availableMembers[0]?.value ?? null);
    setPercOwnership(0);
    setOpen(true);
  };

  const onAddOwnership = () => {
    const newList = [
      ...ownerships.map((o) => ({ user_id: o.user_id, perc_ownership: o.perc_ownership })),
      { user_id: selectedMemberId, perc_ownership: parseFloat(percOwnership) },
    ];
    onUpdate({ ownerships_payload: newList }, null);
    handleClose();
  };

  // EDIT
  const onEditClicked = (userId) => {
    const existing = ownerships.find((o) => o.user_id === userId);
    setActionType("UPDATE");
    setSelectedMemberId(userId);
    setPercOwnership(existing?.perc_ownership ?? 0);
    setOpen(true);
  };

  const onUpdateOwnership = () => {
    const newList = ownerships.map((o) =>
      o.user_id === selectedMemberId
        ? { user_id: o.user_id, perc_ownership: parseFloat(percOwnership) }
        : { user_id: o.user_id, perc_ownership: o.perc_ownership }
    );
    onUpdate({ ownerships_payload: newList }, null);
    handleClose();
  };

  // DELETE
  const onDeleteOwnership = (userId) => {
    const action = () => {
      const newList = ownerships
        .filter((o) => o.user_id !== userId)
        .map((o) => ({ user_id: o.user_id, perc_ownership: o.perc_ownership }));
      onUpdate({ ownerships_payload: newList }, null);
    };
    dispatch(
      openWarningAlert({
        confirmText: t("single.confirm_remove"),
        confirmAction: action,
      })
    );
  };

  const rows = ownerships.map((o) => {
    const member = (members ?? []).find((m) => m.id === o.user_id);
    return {
      id: o.user_id,
      name: member?.name ?? o.user_id,
      perc: o.perc_ownership,
    };
  });

  const columns = [
    {
      key: "name",
      label: t("single.ownerships.table.member"),
      render: (v) => (
        <div className="rec-cell-member">
          <div className="rec-av-sm" style={{ background: avColor(v) }}>
            {initials(v)}
          </div>
          <b>{v}</b>
        </div>
      ),
    },
    {
      key: "perc",
      label: t("single.ownerships.table.perc_ownership"),
      align: "right",
      render: (v) => <Chip tone="teal">{`${v}%`}</Chip>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit", { defaultValue: "Editar" })}
            onClick={(e) => {
              e.stopPropagation();
              onEditClicked(row.id);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.delete", { defaultValue: "Remover" })}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteOwnership(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <SectionCard
      id="ownerships"
      icon="groups"
      title={t("single.ownerships.title")}
      action={
        <Button
          size="sm"
          onClick={onAddClicked}
          disabled={totalOwnership >= 100 || isLoading || !members}
        >
          <Icon name="add" />
          {t("single.ownerships.add_button")}
        </Button>
      }
    >
      <p className="text-[12.5px] text-ink-3 leading-[1.5] -mt-2 mb-4">
        {t("single.ownerships.total_owned", { value: totalOwnership })}
      </p>
      <div className="-mx-[var(--pad-card)] -mb-[var(--pad-card)]">
        <DataTable
          columns={columns}
          rows={rows}
          footer={false}
          search={false}
          onRow={(row) => navigate(`/communities/${community.id}/members/${row.id}`)}
        />
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        title={
          actionType === "CREATE"
            ? t("single.ownerships.modal.title_add")
            : t("single.ownerships.modal.title_edit")
        }
        maxWidth={460}
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              {t("new.cancel", { ns: "community" })}
            </Button>
            <Button
              disabled={!selectedMemberId || !isPercValid || isLoading}
              onClick={actionType === "CREATE" ? onAddOwnership : onUpdateOwnership}
            >
              {actionType === "CREATE"
                ? t("new.create", { ns: "community" })
                : t("new.update", { ns: "community" })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-[14px]">
          <FieldSearchSelect
            label={t("single.ownerships.table.member")}
            options={actionType === "CREATE" ? availableMembers : editingMemberOption}
            value={selectedMemberId ?? ""}
            onChange={setSelectedMemberId}
            disabled={actionType === "UPDATE"}
          />
          <FieldText
            label={`${t("single.ownerships.table.perc_ownership")} (max: ${maxPercAllowed}%)`}
            type="number"
            min={0}
            max={maxPercAllowed}
            suffix="%"
            value={percOwnership}
            onChange={setPercOwnership}
          />
        </div>
      </Modal>
    </SectionCard>
  );
}

OwnershipsSection.defaultProps = {
  data: null,
  isLoading: false,
};

OwnershipsSection.propTypes = {
  data: PropTypes.shape({
    ownerships: PropTypes.arrayOf(
      PropTypes.shape({
        user_id: PropTypes.string,
        perc_ownership: PropTypes.number,
      })
    ),
    total_ownership: PropTypes.number,
  }),
  onUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default OwnershipsSection;
